import { matchingSeason, matchingSeasons } from "@/lib/pricing/season-repeat";
import type { PricingSeason, PricingSeasonRate, ProductPricing } from "@/types";
// type-only — daily-matrix.ts가 이 파일을 import 하므로 런타임 순환을 만들지 않는다
import type { DailyTier } from "./daily-matrix";

/**
 * Calculate the number of rental days between two dates (inclusive).
 *
 * Both start and end dates count as rental days.
 * e.g., Jan 14 → Jan 15 = 2 days
 */
export function calculateRentalDays(
  pickupDate: Date | undefined,
  returnDate: Date | undefined
): number {
  if (!pickupDate || !returnDate) return 0;
  if (isNaN(pickupDate.getTime()) || isNaN(returnDate.getTime())) return 0;

  // Compare calendar dates, not elapsed milliseconds: time-of-day and DST
  // must not move a 6-night/7-day booking into a different price tier.
  const pickupDay = Date.UTC(
    pickupDate.getFullYear(), pickupDate.getMonth(), pickupDate.getDate()
  );
  const returnDay = Date.UTC(
    returnDate.getFullYear(), returnDate.getMonth(), returnDate.getDate()
  );
  const days = Math.round((returnDay - pickupDay) / (1000 * 60 * 60 * 24)) + 1;

  return Math.max(0, days);
}

/**
 * Get the applicable daily rate for a given rental duration
 * based on tiered pricing structure.
 *
 * Pricing tiers are defined by min_days:
 * - min_days: 1, price_per_day: 30 → for 1-2 days rental
 * - min_days: 3, price_per_day: 25 → for 3+ days rental
 *
 * The function finds the highest tier where days >= min_days
 */
export function getDailyRateForDays(
  days: number,
  pricing: ProductPricing[]
): number {
  if (!pricing || pricing.length === 0) {
    return 0;
  }

  // Filter to daily pricing only and ensure min_days exists
  const dailyPricing = pricing.filter(
    (p) =>
      p.pricing_type === "daily" &&
      p.min_days != null &&
      p.price_per_day != null
  );

  if (dailyPricing.length === 0) {
    return 0;
  }

  // Sort by min_days descending to find the highest applicable tier first
  const sortedPricing = [...dailyPricing].sort(
    (a, b) => (b.min_days || 0) - (a.min_days || 0)
  );

  // Find the first tier where days >= min_days
  const applicableTier = sortedPricing.find(
    (p) => days >= (p.min_days || 1)
  );

  return applicableTier?.price_per_day || 0;
}

/**
 * Calculate total rental price for a given number of days
 * considering tiered pricing structure.
 */
export function calculateRentalPrice(
  days: number,
  quantity: number,
  pricing: ProductPricing[]
): number {
  const dailyRate = getDailyRateForDays(days, pricing);
  return days * dailyRate * quantity;
}

/**
 * Get the base daily rate (min_days = 1) for display purposes
 */
export function getBaseDailyRate(pricing: ProductPricing[]): number {
  if (!pricing || pricing.length === 0) {
    return 0;
  }

  const basePricing = pricing.find(
    (p) => p.pricing_type === "daily" && p.min_days === 1
  );

  return basePricing?.price_per_day || 0;
}

// ============================================================================
// Seasonal (성수기) pricing
//
// 확정 정책:
// - daily: min_days 티어는 "전체 대여일수"로 기존과 동일하게 선택. 각 대여일의
//   일 요금만 그 날짜가 속한 시즌의 재정의(해당 티어 rate 행이 있을 때)로
//   덮어쓰고 날짜별 합산. 재정의 없는 티어/날짜는 기존가 유지 (부분 재정의).
// - timeslot: 픽업일이 시즌 내이고 해당 슬롯(display_order) rate 행이 있으면
//   슬롯가 재정의.
// - v2(09-21, SEASON_V2_CONTRACT.md): rate 행이 `sub_display_order`(세부 상품 축)를
//   가진다. daily v2 = 세부 상품의 day_prices 티어 + (sub, min_days) 행,
//   timeslot v2 = (sub, option display_order) 행. v1 매칭은 NULL 행만, v2 매칭은
//   N 행만 본다 — 절대 섞지 않는다(전환 잔재 v1 행이 v2 가격을 덮지 않도록).
//
// ⚠️ 이 알고리즘은 서버 검증(actions/checkout.ts, actions/cart.ts — 같은 함수
// import)과 Edge webhook(_shared/authoritative-pricing.ts — Deno 포팅본)에서
// 동일하게 실행된다. 수정 시 세 곳 모두 갱신하지 않으면 CART_PRICE_MISMATCH.
// ============================================================================

/** 로컬 자정 기준 YYYY-MM-DD (시즌 기간 비교용 — 타임존 드리프트 방지) */
export function toLocalDateString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** 해당 날짜(YYYY-MM-DD)가 속한 시즌 반환 (없으면 null). 기간은 양끝 포함 */
export function findSeasonForDate(
  seasons: PricingSeason[] | undefined,
  dateStr: string
): PricingSeason | null {
  if (!seasons || seasons.length === 0) return null;
  return (
    matchingSeason(seasons, dateStr)
  );
}

/**
 * rate 행의 세부 상품 축 — 키가 없는 구 데이터/RPC 행은 NULL(v1)로 취급.
 * v1 조회(null)는 NULL 행만, v2 조회(N)는 N 행만 엄격 매칭한다.
 */
function rateSubMatches(
  rate: PricingSeasonRate,
  subDisplayOrder: number | null
): boolean {
  return (rate.sub_display_order ?? null) === subDisplayOrder;
}

/**
 * 시즌에서 daily 티어(min_days) 재정의 일 요금 조회 (없으면 null).
 * `subDisplayOrder`: v1 = null, daily v2 = 선택한 세부 상품 좌표.
 */
function getSeasonDailyRate(
  season: PricingSeason,
  minDays: number,
  subDisplayOrder: number | null
): number | null {
  const rate = season.rates.find(
    (r) =>
      r.pricing_type === "daily" &&
      r.min_days === minDays &&
      rateSubMatches(r, subDisplayOrder)
  );
  return rate ? rate.price : null;
}

/** 전체 대여일수로 선택된 티어의 min_days 반환 (티어 없으면 null) */
export function getApplicableTierMinDays(
  days: number,
  pricing: ProductPricing[]
): number | null {
  const dailyPricing = (pricing || []).filter(
    (p) =>
      p.pricing_type === "daily" &&
      p.min_days != null &&
      p.price_per_day != null
  );
  if (dailyPricing.length === 0) return null;
  const sorted = [...dailyPricing].sort(
    (a, b) => (b.min_days || 0) - (a.min_days || 0)
  );
  const tier = sorted.find((p) => days >= (p.min_days || 1));
  return tier?.min_days ?? null;
}

export interface SeasonalPriceResult {
  total: number;
  /** 하나라도 시즌 재정의 요금이 적용되었는지 (결제 화면 안내용) */
  seasonalApplied: boolean;
}

/**
 * daily 대여일별 일 요금 배열 (성수기 재정의 반영, 공급가/기존가 기준)
 *
 * 마크업 모델은 "일 단위" 공급가에 마크업을 적용해야 하므로, 서버 검증
 * (actions/checkout.ts 등)은 이 배열을 받아 일별로 마크업 후 합산한다.
 */
export function getSeasonalDailyRates(
  pickupDate: Date,
  days: number,
  baseRate: number,
  tierMinDays: number | null,
  seasons: PricingSeason[] | undefined,
  /** v1 = null (상품 단위 행만), daily v2 = 세부 상품 좌표 (그 sub 행만) */
  subDisplayOrder: number | null
): { rates: number[]; seasonalApplied: boolean } {
  const rates: number[] = [];
  let seasonalApplied = false;

  for (let i = 0; i < days; i++) {
    const day = new Date(
      pickupDate.getFullYear(),
      pickupDate.getMonth(),
      pickupDate.getDate() + i
    );
    const override = tierMinDays == null ? null : matchingSeasons(seasons ?? [], toLocalDateString(day))
      .map((season) => getSeasonDailyRate(season, tierMinDays, subDisplayOrder))
      .find((price) => price != null) ?? null;

    if (override != null) {
      rates.push(override);
      seasonalApplied = true;
    } else {
      rates.push(baseRate);
    }
  }

  return { rates, seasonalApplied };
}

/**
 * daily 상품의 성수기 반영 총액 (날짜별 합산)
 *
 * 티어는 전체 대여일수로 결정하고, 대여일 각각에 대해 그 날짜가 성수기이며
 * 해당 티어의 재정의 요금이 있으면 재정의가, 아니면 기존 티어 요금이 적용된다.
 * 시즌이 없으면 기존 calculateRentalPrice와 완전히 동일한 결과.
 */
export function calculateSeasonalRentalPrice(
  pickupDate: Date | undefined,
  returnDate: Date | undefined,
  quantity: number,
  pricing: ProductPricing[],
  seasons: PricingSeason[] | undefined
): SeasonalPriceResult {
  const days = calculateRentalDays(pickupDate, returnDate);
  if (days <= 0 || !pickupDate) {
    return { total: 0, seasonalApplied: false };
  }

  const baseRate = getDailyRateForDays(days, pricing);

  if (!seasons || seasons.length === 0) {
    return { total: days * baseRate * quantity, seasonalApplied: false };
  }

  const tierMinDays = getApplicableTierMinDays(days, pricing);
  const { rates, seasonalApplied } = getSeasonalDailyRates(
    pickupDate,
    days,
    baseRate,
    tierMinDays,
    seasons,
    null
  );
  const total = rates.reduce((sum, rate) => sum + rate, 0);

  return { total: total * quantity, seasonalApplied };
}

// ── Daily v2 (세부 상품별 기간 티어) ────────────────────────────────────────

/**
 * v2 티어를 `getDailyRateForDays`/`getApplicableTierMinDays`가 읽는 모양
 * (ProductPricing)으로 어댑트. **수식 재사용을 위한 소스 주입 전용** —
 * 저장/전송 용도로 쓰지 말 것. (daily-matrix.ts가 re-export)
 */
export function toDailyPricingRows(tiers: DailyTier[]): ProductPricing[] {
  return tiers.map(
    (tier) =>
      ({
        pricing_type: "daily",
        min_days: tier.minDays,
        price_per_day: tier.pricePerDay,
      }) as unknown as ProductPricing
  );
}

/**
 * daily v2 대여일별 일 요금 배열 (성수기 재정의 반영).
 *
 * `getSeasonalDailyRates`와 **정확히 같은 규칙**에 세부 상품 축만 더한 것:
 * - 티어(min_days)는 선택한 세부 상품의 티어 배열로 전체 대여일수에서 선택
 * - 기본가 = 그 티어의 일 단가(`getDailyRateForDays` 수식 그대로)
 * - 각 대여일: 시즌에 (daily, sub, tier) 행이 있으면 그 값, 없으면 기본가
 * 티어가 없거나 0원이면 기본가 0 → 호출측이 fail-closed 처리한다.
 */
export function getSeasonalDailyV2Rates(
  pickupDate: Date,
  days: number,
  tiers: DailyTier[],
  subDisplayOrder: number,
  seasons: PricingSeason[] | undefined
): { rates: number[]; seasonalApplied: boolean } {
  const rows = toDailyPricingRows(tiers || []);
  const baseRate = getDailyRateForDays(days, rows);
  const tierMinDays = getApplicableTierMinDays(days, rows);
  return getSeasonalDailyRates(
    pickupDate,
    days,
    baseRate,
    tierMinDays,
    seasons,
    subDisplayOrder
  );
}

/**
 * daily v2 상품의 성수기 반영 총액 (날짜별 합산 × 수량) —
 * `calculateSeasonalRentalPrice`의 세부 상품 축 버전.
 */
export function calculateSeasonalDailyV2Price(
  pickupDate: Date | undefined,
  returnDate: Date | undefined,
  quantity: number,
  tiers: DailyTier[],
  subDisplayOrder: number,
  seasons: PricingSeason[] | undefined
): SeasonalPriceResult {
  const days = calculateRentalDays(pickupDate, returnDate);
  if (days <= 0 || !pickupDate) {
    return { total: 0, seasonalApplied: false };
  }
  const { rates, seasonalApplied } = getSeasonalDailyV2Rates(
    pickupDate,
    days,
    tiers,
    subDisplayOrder,
    seasons
  );
  const total = rates.reduce((sum, rate) => sum + rate, 0);
  return { total: total * quantity, seasonalApplied };
}

/**
 * timeslot 슬롯의 성수기 반영 단가
 *
 * 픽업일이 시즌 내이고 해당 슬롯(display_order)의 재정의 rate가 있으면 재정의가,
 * 아니면 기존 슬롯가.
 * - v1: displayOrder = product_pricing.display_order, subDisplayOrder = null
 * - v2: displayOrder = 이용 시간 옵션 좌표, subDisplayOrder = 세부 상품 좌표
 *   (셀 가격이 basePrice)
 */
export function getSeasonalTimeslotPrice(
  basePrice: number,
  displayOrder: number | null | undefined,
  pickupDate: Date | undefined,
  seasons: PricingSeason[] | undefined,
  subDisplayOrder: number | null
): SeasonalPriceResult {
  if (!pickupDate || displayOrder == null || !seasons || seasons.length === 0) {
    return { total: basePrice, seasonalApplied: false };
  }

  const rate = matchingSeasons(seasons, toLocalDateString(pickupDate)).flatMap((season) => season.rates).find(
    (r) =>
      r.pricing_type === "timeslot" &&
      r.display_order === displayOrder &&
      rateSubMatches(r, subDisplayOrder)
  );
  if (!rate) {
    return { total: basePrice, seasonalApplied: false };
  }

  return { total: rate.price, seasonalApplied: true };
}
